import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../i18n/LocalizationProvider';
import type { TranslationContent } from '../i18n/translations';
import { allocateTests, getStore } from '../data/portalStore';
import type { PortalStore } from '../data/portalStore';
import { useAuth } from '../auth/AuthContext';

export default function OrgUserPage() {
  const { t } = useTranslation();
  const labels = t<TranslationContent['orgUser']>('orgUser');
  const { session } = useAuth();
  const [store, setStore] = useState<PortalStore>(() => getStore());
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');

  useEffect(() => {
    setStore(getStore());
  }, []);

  const org = store.orgs[0];
  const subusers = store.subusers.filter(item => item.orgId === org?.id);
  const profiles = store.profiles.filter(item => item.orgId === org?.id);

  const totals = useMemo(() => {
    const revealed = profiles.filter(profile => profile.status === 'revealed').length;
    return { profiles: profiles.length, revealed };
  }, [profiles]);

  const handleAllocate = (event: React.FormEvent<HTMLFormElement>, subuserId: string) => {
    event.preventDefault();
    setMessage('');
    const raw = allocations[subuserId];
    const amount = Number(raw);
    if (!org || !session || Number.isNaN(amount) || amount <= 0) {
      setMessage(labels.allocate.error);
      return;
    }
    const result = allocateTests(org.id, subuserId, amount, session.userId);
    if (result.error) {
      setMessage(labels.allocate.error);
      return;
    }
    setStore(result.store);
    setAllocations(prev => ({ ...prev, [subuserId]: '' }));
    setMessage(labels.allocate.success);
  };

  if (!org) {
    return (
      <div className="panel">
        <p className="text-muted">{labels.empty}</p>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="section-heading">
        <div>
          <span className="badge">{labels.badge}</span>
          <h2>{labels.title}</h2>
          <p className="text-muted">{labels.description}</p>
        </div>
        <div className="metric-card" style={{ margin: 0, padding: '1rem 1.25rem' }}>
          <span>{labels.orgSummary.available}</span>
          <strong>{org.availableTests}</strong>
        </div>
      </div>

      <div className="metric-grid" style={{ marginBottom: '2rem' }}>
        <div className="metric-card">
          <span>{labels.orgSummary.org}</span>
          <strong>{org.name}</strong>
        </div>
        <div className="metric-card">
          <span>{labels.orgSummary.totalIssued}</span>
          <strong>{org.totalIssued}</strong>
        </div>
        <div className="metric-card">
          <span>{labels.orgSummary.profiles}</span>
          <strong>{totals.profiles}</strong>
        </div>
        <div className="metric-card">
          <span>{labels.orgSummary.revealed}</span>
          <strong>{totals.revealed}</strong>
        </div>
      </div>

      {message && <p className="text-muted">{message}</p>}

      <div className="panel" style={{ boxShadow: 'none', marginBottom: '2rem' }}>
        <h3>{labels.subusers.title}</h3>
        <table>
          <thead>
            <tr>
              <th>{labels.subusers.table.name}</th>
              <th>{labels.subusers.table.balance}</th>
              <th>{labels.subusers.table.allocate}</th>
            </tr>
          </thead>
          <tbody>
            {subusers.map(subuser => (
              <tr key={subuser.id}>
                <td>{subuser.name}</td>
                <td>{subuser.allocatedTests}</td>
                <td>
                  <form
                    className="form-grid"
                    style={{ gridTemplateColumns: '1fr auto' }}
                    onSubmit={event => handleAllocate(event, subuser.id)}
                  >
                    <input
                      type="number"
                      min={1}
                      value={allocations[subuser.id] ?? ''}
                      onChange={event =>
                        setAllocations(prev => ({ ...prev, [subuser.id]: event.target.value }))
                      }
                      placeholder={labels.allocate.amountPlaceholder}
                    />
                    <button type="submit" className="button-secondary">
                      {labels.allocate.submit}
                    </button>
                  </form>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="panel" style={{ boxShadow: 'none' }}>
        <h3>{labels.profiles.title}</h3>
        <table>
          <thead>
            <tr>
              <th>{labels.profiles.table.profile}</th>
              <th>{labels.profiles.table.subuser}</th>
              <th>{labels.profiles.table.status}</th>
              <th>{labels.profiles.table.tier}</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map(profile => (
              <tr key={profile.id}>
                <td>{profile.name}</td>
                <td>{store.subusers.find(item => item.id === profile.subuserId)?.name}</td>
                <td>{labels.profiles.status[profile.status]}</td>
                <td>{profile.status === 'revealed' ? profile.riskTier : labels.profiles.hidden}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
